/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IGeneric,
    IAny
} from '@msdyn365-commerce/core';
import { ProductUnitOfMeasure } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getProductUnitsOfMeasureAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * Input for getProductUnitsOfMeasure data action.
 */
export class GetProductUnitsOfMeasureInput implements IActionInput {
    public productId: number;

    constructor(productId: number) {
        this.productId = productId;
    }

    public getCacheKey = () => `ProductUnitsOfMeasure_${this.productId}`;

    public getCacheObjectType = () => 'ProductUnitOfMeasure';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Gets productId from action context (same pattern as get-selected-variant).
 */
function getProductIdFromContext(inputData: ICreateActionContext<IGeneric<IAny>>): number | undefined {
    const context = inputData.requestContext;
    const productId =
        context.query?.productId ??
        (context.urlTokens?.pageType === 'Product' ? context.urlTokens?.itemId : undefined) ??
        (inputData.config as { productId?: string })?.productId;
    if (productId !== undefined && productId !== null) {
        const id = typeof productId === 'string' ? parseInt(productId, 10) : productId;
        return isNaN(id) ? undefined : id;
    }
    return undefined;
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetProductUnitsOfMeasureInput => {
    const productId = getProductIdFromContext(inputData);
    if (!productId) {
        throw new Error('[getProductUnitsOfMeasure] Unable to create input: no productId found on request context or config');
    }
    return new GetProductUnitsOfMeasureInput(productId);
};

/**
 * Fetches the list of Units of Measure (UOMs) for a product using CRT GetProductUnitsOfMeasure API.
 */
export async function getProductUnitsOfMeasureAction(
    input: GetProductUnitsOfMeasureInput,
    ctx: IActionContext
): Promise<ProductUnitOfMeasure[]> {
    if (!input?.productId) {
        return [];
    }
    try {
        const unitOfMeasureSearchCriteria = {};
        const result = await getProductUnitsOfMeasureAsync(
            { callerContext: ctx, queryResultSettings: {} },
            input.productId,
            unitOfMeasureSearchCriteria
        );
        const uoms = result ?? [];
        return uoms;
    } catch (error) {
        console.error('[getProductUnitsOfMeasure] API error', { productId: input.productId, error });
        ctx.telemetry?.error?.(error instanceof Error ? error.message : String(error));
        return [];
    }
}

export const getProductUnitsOfMeasureDataAction = createObservableDataAction({
    id: 'get-product-units-of-measure',
    action: getProductUnitsOfMeasureAction as IAction<ProductUnitOfMeasure[]>,
    input: createInput
});

export default getProductUnitsOfMeasureDataAction;
